import {
  useAnswerOnClarificationMutation,
  useRequestClarificationMutation,
} from "@/app/api";
import { DealRequest } from "@/app/api/types";
import { getFetchEror } from "@/app/helpers";
import { useOpen } from "@/app/hooks";
import { useNotification } from "@/shared/contexts/NotificationContext";
import { FormTextInput } from "@/shared/inputs/form-text-input";
import ModalTemplate from "@/shared/modal/modal-template";
import { UIFormLabel } from "@/shared/ui/ui-form-label";
import { Dialog, FormControl } from "@mui/material";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ClarificationModalRef } from "./types";

interface Props {
  deal: DealRequest;
}

export const FactorRequestClearifyModal = forwardRef<
  ClarificationModalRef,
  Props
>(({ deal }, ref) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { open, setOpen } = useOpen();
  const { control, reset, handleSubmit } = useForm<{ comment: string }>();

  const [commentUnique, setCommentUnique] = useState<number | null>(null);

  const [requestClarification, { isLoading }] =
    useRequestClarificationMutation();
  const [answerOnClarification, { isLoading: isAnserLoadinf }] =
    useAnswerOnClarificationMutation();

  useImperativeHandle(
    ref,
    () => ({
      open: onOpen,
      close: onClose,
    }),
    []
  );

  const onOpen: ClarificationModalRef["open"] = (u) => {
    setCommentUnique(u || null);
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setTimeout(() => {
      reset();
    }, 300);
  };

  const onSubmit = async (data: { comment: string }) => {
    try {
      if (!commentUnique) {
        await requestClarification({
          id: deal.unique,
          comment: data.comment,
        }).unwrap();
      } else {
        await answerOnClarification({
          id: commentUnique,
          answer: data.comment,
        }).unwrap();
      }
      onClose();
      showNotification(
        t(commentUnique ? "reply_sent" : "clarification_request_sent"),
        "success"
      );
    } catch (error) {
      showNotification(getFetchEror(error), "error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: 920,
          width: "100%",
        },
      }}
    >
      <ModalTemplate
        title={t(
          commentUnique ? "send_reply_to_factor" : "request_clarification"
        )}
        onClose={onClose}
        onSubmit={handleSubmit(onSubmit)}
        submitBtnText={t("send")}
        loading={isLoading || isAnserLoadinf}
      >
        <FormControl fullWidth required>
          <UIFormLabel>{t("comment")}</UIFormLabel>
          <FormTextInput
            control={control}
            name="comment"
            multiline
            rows={5}
            rules={{
              required: true,
            }}
          />
        </FormControl>
      </ModalTemplate>
    </Dialog>
  );
});
